import { Button } from '@UILayer/Inputs/Button';
import { DOM_ID } from '@UILayer/UILayer.types';
import cn from 'classnames';
import React, { useEffect, useReducer } from 'react';
import { useTranslation } from 'react-i18next';

// CSS
import styles from '@UILayer/UILayer.scss';

// REDUX & MANAGERS
import { CurrencyUtils } from '@Core/Currencies/CurrencyUtils';
import { EButtonEvents } from '@Managers/Events/ButtonEvents';
import EventsCenter from '@Managers/Events/EventsCenter';
import { getBetState, getGlobalStyles, setPopupPanel } from '@Managers/Redux/AppState/AppState';
import { useAppDispatch, useAppSelector } from '@Managers/Redux/Hooks';
import TextField from '@UILayer/Inputs/TextField/TextField';
import { IPopupPanelProperties } from '../PopupPanel.types';
import { IBetPanelState } from './BetPanel.types';

const BetPanel: React.FC<IPopupPanelProperties> = ({ baseFontSize }) => {
    const [state, setState] = useReducer((state: IBetPanelState, newState: any) => ({ ...state, ...newState }), {
        betSteps: [],
        index: 0,
        maxIndex: 0,
        listFontSize: undefined
    });

    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const gameStoreBetState = useAppSelector(getBetState);
    const globalStyles = useAppSelector(getGlobalStyles);

    useEffect(() => {
        const betData = gameStoreBetState;
        const betCurrency = gameStoreBetState.currencySymbol;
        const betsArray = [...betData.availableBetSteps];

        if (betData) {
            setState({
                index: betData.index,
                maxIndex: betData.maxIndex,
                betSteps: betsArray.map(({ formatted, raw }) => {
                    if (CurrencyUtils.getConfig().currencyHeaderLabel) {
                        return CurrencyUtils.formatRawCurrencyValue(raw, { withSymbol: false });
                    } else {
                        return formatted;
                    }
                })
            });
        }
    }, [gameStoreBetState]);

    function setBet(index: number) {
        EventsCenter.emit(EButtonEvents.BET_INDEX_BUTTON_PRESSED, { betIndex: index });
        setTimeout(() => {
            dispatch(setPopupPanel(false));
        }, 150);
    }

    function setListFontsize(value: number) {
        setState({ listFontSize: value });
    }

    function baseFont() {
        return baseFontSize ? { fontSize: `${baseFontSize}px` } : {};
    }

    return (
        <>
            <div className={styles.popup_panel_content_inner}>
                <div className={cn(styles.bet_list, styles.popup_content_list)}>
                    {state.betSteps.map((element, index) => (
                        <div
                            key={index}
                            className={cn(styles.bet_button, styles.popup_panel_list_button, globalStyles.wcaMode, index === state.index ? styles.active : '')}
                        >
                            <Button
                                dataElement={element}
                                key={index}
                                id={`ui_button_bet_list_${index}`}
                                description={`${t('WCA_SET_BET_TO')} ${element}`}
                                className={cn(globalStyles.wcaMode)}
                                onChange={() => setBet(index)}
                                focus={index === 0 ? true : false}
                            >
                                {index === state.maxIndex ? (
                                    <TextField useSpanElement={true} onResizeEnd={(value: number) => setListFontsize(value)}>
                                        {element}
                                    </TextField>
                                ) : (
                                    <>
                                        <span style={state.listFontSize && index !== state.maxIndex ? { fontSize: `${state.listFontSize}px` } : {}}>
                                            {element}
                                        </span>
                                    </>
                                )}
                            </Button>
                        </div>
                    ))}
                </div>
                <div className={styles.popup_panel_actions}>
                    <Button
                        style={baseFont()}
                        id={DOM_ID.BUTTON_SET_MAX_BET}
                        description={t('WCA_SET_MAX_BET')}
                        className={cn(styles.popup_panel_action_button, styles.play_action_button)}
                        onChange={() => setBet(state.maxIndex)}
                    >
                        {t('MAX_BET')}
                    </Button>
                </div>
            </div>
        </>
    );
};

export default BetPanel;
